import {
  Box,
  Grid,
  Skeleton,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { useEffect, useState } from "react";

import {
  PlayListvideoListTableStyle,
  PlayListvideoCellStyle,
  PlayListdescriptionCellStyle,
  tableCheckBox,
  ViewPlayListMainContainerStyles,
  HeightLocked100,
} from "./css";

import {
  GetSinglePlayList,
  RemoveVideoFromList,
} from "../../services/ContentServices";
import { Video } from "./types";
import VideoPlayListDrawer from "./VideoPlayListDrawer";
import EditPlaylist from "./EditPlaylist";
import { useSnackbar } from "notistack";
import { PlaylistData } from "./types";
import { TableBodyVideoList } from "../../utils/ContentCardList";

interface ViewPlayListProps {
  setShowViewPlayList: React.Dispatch<React.SetStateAction<boolean>>;
  selectedPlayList: string;
}

const ViewPlayList: React.FC<ViewPlayListProps> = ({
  setShowViewPlayList,
  selectedPlayList,
}) => {
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const [storedVidIds, setStoredVidIds] = useState<string[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [playlistData, setPlaylistData] = useState<PlaylistData>({
    name: "",
    coverPicture: null,
    coverPicturePreview: null,
    description: "",
    availableForVisitors: false,
  });

  useEffect(() => {
    fetchPlayListData();
  }, []);

  const fetchUpdatedData = async () => {
    await fetchPlayListData();
  };

  const fetchPlayListData = async () => {
    try {
      setIsLoading(true);
      const response = await GetSinglePlayList({ selectedPlayList });
      setPlaylistData({
        name: response.name,
        coverPicture: null,
        coverPicturePreview: response.thumbnailUrl,
        description: response.description,
        availableForVisitors: response.availableForVisitors,
      });
      setStoredVidIds(response.videos.map((vid) => vid.id));
      setVideos(response.videos);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to fetch playlist data", { variant: "error" });
    }
  };

  const openDrawer = () => {
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  return (
    <>
      <Box sx={ViewPlayListMainContainerStyles}>
        <Grid container sx={HeightLocked100}>
          <Grid item xs={12} md={5}>
            <EditPlaylist
              setShowViewPlayList={setShowViewPlayList}
              selectedPlayList={selectedPlayList}
              playlistData={playlistData}
              onDataUpdate={fetchUpdatedData}
              setPlaylistData={setPlaylistData}
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <VideoList
              openDrawer={openDrawer}
              videos={videos}
              loading={isLoading}
              selectedPlayList={selectedPlayList}
              setVideos={setVideos}
              setStoredVidIds={setStoredVidIds}
            />
          </Grid>
          <VideoPlayListDrawer
            open={drawerOpen}
            onClose={closeDrawer}
            onDataUpdate={fetchUpdatedData}
            storedVidIds={storedVidIds}
            selectedPlayList={selectedPlayList}
          />
        </Grid>
      </Box>
    </>
  );
};

export default ViewPlayList;

const VideoList = ({
  openDrawer,
  videos,
  loading,
  selectedPlayList,
  setVideos,
  setStoredVidIds,
}: {
  openDrawer: () => void;
  videos: Video[];
  loading: boolean;
  selectedPlayList: string;
  setVideos: (videos: Video[]) => void;
  setStoredVidIds: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const handleRemoveFromPlaylist = async (id: string): Promise<number> => {
    let result = await RemoveVideoFromList({
      playlistId: selectedPlayList as string,
      videoId: id,
    });

    if (result == 200) {
      setVideos(videos.filter((video) => video.id !== id));

      setStoredVidIds((prevStoredVidIds) =>
        prevStoredVidIds.filter((vidId) => vidId !== id)
      );
      enqueueSnackbar("Successfully ! Video has been removed", {
        variant: "success",
      });
      return 200;
    } else {
      enqueueSnackbar("Failed!! Video has not removed", {
        variant: "error",
      });
      return 401;
    }
  };

  return (
    <>
      <Box sx={PlayListvideoListTableStyle}>
        <TableContainer sx={{ height: 90 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={tableCheckBox}></TableCell>
                <TableCell sx={PlayListvideoCellStyle}>
                  {"PlayList"} Videos
                </TableCell>
                <TableCell
                  sx={{ ...PlayListdescriptionCellStyle, textAlign: "right" }}
                  onClick={openDrawer}
                >
                  <b> + Add More</b>
                </TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </TableContainer>

        <Box
          sx={{
            height: "100%",
            overflowY: "auto",
            width: "80%",
            margin: "auto",
            scrollbarWidth: "none",
            marginTop: 2,
          }}
        >
          <Box>
            <TableBodyVideoList
              loading={loading}
              videos={videos}
              callBackchange={handleRemoveFromPlaylist}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

const VideoTableRowSkeleton = () => (
  <TableRow>
    <TableCell sx={tableCheckBox}></TableCell>
    <TableCell sx={PlayListvideoCellStyle}>
      <Skeleton variant="rectangular" width={"100%"} height={100} />
    </TableCell>
    <TableCell sx={PlayListdescriptionCellStyle}>
      <Box>
        <Skeleton variant="text" width={150} height={20} />
        <Skeleton variant="text" width={300} height={40} />
      </Box>
    </TableCell>
  </TableRow>
);

const renderSkeletonRows = () =>
  Array.from({ length: 25 }).map((_, index) => (
    <VideoTableRowSkeleton key={index} />
  ));
